import { saleApi } from '@/app/api/saleApi';
import type { SharedCart } from './tableSessionApi';
import type { SelectedOption } from '@/types/cart';
import type { ServiceChoice } from '@/types/order';
import { toWireCartOptions } from '@/features/checkout/cartOptions';

// SC-169 — shared table cart mutations (guest-Bearer auth). Reads go through
// getTableSession (the existing 5s poll, which now carries `shared_cart`), so
// every mutation invalidates the TableSession tag to refresh the poll.

const guestHeaders = (guestToken: string) => ({ Authorization: `Bearer ${guestToken}` });

export interface SubmitSharedCartResult {
  // The backend returns the full anchor order (OrderOutputSerializer). SC-346 reads
  // items[].round_number to pin the just-sent round into the confirmation URL.
  order: { uuid: string; items?: { round_number?: number | null }[]; held_rounds?: number[] };
  order_access_token?: string;
}

export const sharedCartApiSlice = saleApi.injectEndpoints({
  endpoints: (build) => ({
    addSharedCartLine: build.mutation<
      SharedCart,
      { guestToken: string; product_uuid: string; quantity?: number; options?: unknown[]; comment?: string; nickname?: string }
    >({
      query: ({ guestToken, ...body }) => ({
        url: '/sale/order/table-session/cart/lines/',
        method: 'POST',
        headers: guestHeaders(guestToken),
        body,
      }),
      invalidatesTags: ['TableSession'],
    }),

    updateSharedCartLineQty: build.mutation<
      SharedCart,
      { guestToken: string; lineId: string; quantity: number }
    >({
      query: ({ guestToken, lineId, quantity }) => ({
        url: `/sale/order/table-session/cart/lines/${lineId}/`,
        method: 'PATCH',
        headers: guestHeaders(guestToken),
        body: { quantity },
      }),
      invalidatesTags: ['TableSession'],
    }),

    removeSharedCartLine: build.mutation<
      SharedCart,
      { guestToken: string; lineId: string }
    >({
      query: ({ guestToken, lineId }) => ({
        url: `/sale/order/table-session/cart/lines/${lineId}/`,
        method: 'DELETE',
        headers: guestHeaders(guestToken),
      }),
      invalidatesTags: ['TableSession'],
    }),

    submitSharedCart: build.mutation<
      SubmitSharedCartResult,
      {
        guestToken: string;
        recaptchaToken?: string | null;
        customerNote?: string;
        cartOptions?: SelectedOption[];
        serviceChoice?: ServiceChoice;
      }
    >({
      // SC-337: carry the Cloudflare Turnstile token so the backend can block
      // bot/direct-API sends. Empty string when captcha is off (dev/CI) — the
      // backend verify fails open there.
      // SC-567: the sender's note goes out as customer_note, same field the
      // single-guest dine-in checkout uses — omitted when blank.
      // SC-753: les réponses aux questions ciblant « Sur place » partent comme
      // cart_options, même champ que la commande à emporter — omises quand rien
      // n'est coché.
      query: ({ guestToken, recaptchaToken, customerNote, cartOptions, serviceChoice }) => {
        const note = customerNote?.trim() || '';
        const options = toWireCartOptions(cartOptions ?? []);
        return {
          url: '/sale/order/table-session/cart/submit/',
          method: 'POST',
          headers: guestHeaders(guestToken),
          body: {
            recaptcha_token: recaptchaToken ?? '',
            ...(note ? { customer_note: note } : {}),
            ...(options.length ? { cart_options: options } : {}),
            // DF-0586 — omis tant que la table n'a pas eu à choisir.
            ...(serviceChoice ? { service_choice: serviceChoice } : {}),
          },
        };
      },
      invalidatesTags: ['Order', 'TableSession'],
    }),
  }),
});

export const {
  useAddSharedCartLineMutation,
  useUpdateSharedCartLineQtyMutation,
  useRemoveSharedCartLineMutation,
  useSubmitSharedCartMutation,
} = sharedCartApiSlice;
